import { useCallback } from 'react';
import { getDocumentaryService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type {
  Documentary,
  DocumentariesQueryParams,
  DocumentariesData,
  DocumentaryCreateRequest,
  DocumentaryUpdateRequest,
  DocumentaryDetailsResponse,
  DocumentaryType,
  DocumentarySubtype,
} from '../types/api/documentary';
import type {
  InitiateMultipartUploadRequest,
  InitiateMultipartUploadResponse,
  CompleteMultipartUploadRequest,
  VideoVariant,
  VideoMultipartStatusResponse,
  VideoMultipartRegenerateRequest,
  VideoMultipartPresignResponse,
} from '../types/api/movie';

/**
 * Custom hook for documentary management.
 *
 * Integrates DocumentaryService with the DataTable component
 * and provides high-level CRUD helpers for pages.
 */
export function useDocumentaries() {
  const documentaryService = getDocumentaryService();

  /**
   * Fetch function compatible with DataTable.
   *
   * Backend uses 1-based page index; pass through directly.
   */
  const fetchDocumentaries: FetchDataFn<Documentary> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Documentary>> => {
      try {
        const apiParams: DocumentariesQueryParams = {
          page: params.page,
          size: params.pageSize,
        };

        if (params.search) {
          apiParams.keyword = params.search;
        }

        // Extract accessLevel filter if present
        if (params.filters?.accessLevel?.value) {
          const accessLevelValue = params.filters.accessLevel.value;
          if (accessLevelValue && accessLevelValue !== '') {
            apiParams.accessLevel = accessLevelValue;
          }
        }

        // Extract documentaryType filter if present
        if (params.filters?.documentaryType?.value) {
          const documentaryTypeValue = params.filters.documentaryType.value;
          if (documentaryTypeValue && documentaryTypeValue !== '') {
            apiParams.documentaryType = documentaryTypeValue as DocumentaryType;
          }
        }

        // Extract documentarySubtype filter if present
        if (params.filters?.documentarySubtype?.value) {
          const documentarySubtypeValue = params.filters.documentarySubtype.value;
          if (documentarySubtypeValue && documentarySubtypeValue !== '') {
            apiParams.documentarySubtype = documentarySubtypeValue as DocumentarySubtype;
          }
        }

        // Extract contentRegion filter if present ("myanmar" | "international")
        if (params.filters?.contentRegion?.value) {
          const contentRegionValue = params.filters.contentRegion.value;
          if (contentRegionValue === 'myanmar' || contentRegionValue === 'international') {
            apiParams.contentRegion = contentRegionValue;
          }
        }

        const response: DocumentariesData = await documentaryService.getDocumentaries(apiParams);

        return {
          data: response.items,
          pagination: {
            page: response.page,
            pageSize: response.size,
            total: response.totalItems,
            totalPages: response.totalPages,
            hasNext: response.hasNext,
            hasPrev: response.hasPrevious,
          },
        };
      } catch (error) {
        console.error('Failed to fetch documentaries:', error);
        throw new Error('Failed to load documentaries. Please try again.');
      }
    },
    [documentaryService]
  );

  return {
    fetchDocumentaries,
  };
}

/**
 * Hook for documentary actions.
 *
 * Provides functions to perform actions on documentaries.
 */
export function useDocumentaryActions() {
  const documentaryService = getDocumentaryService();

  const createDocumentary = useCallback(
    async (
      payload: DocumentaryCreateRequest,
      thumbnailFile?: File
    ): Promise<Documentary> => {
      try {
        return await documentaryService.createDocumentary(payload, thumbnailFile);
      } catch (error) {
        console.error('Failed to create documentary:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const createDocumentaryJson = useCallback(
    async (payload: DocumentaryCreateRequest): Promise<Documentary> => {
      try {
        return await documentaryService.createDocumentaryJson(payload);
      } catch (error) {
        console.error('Failed to create documentary (JSON):', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const updateDocumentary = useCallback(
    async (
      documentaryId: string,
      payload: DocumentaryUpdateRequest
    ): Promise<Documentary> => {
      try {
        return await documentaryService.updateDocumentary(documentaryId, payload);
      } catch (error) {
        console.error('Failed to update documentary:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const softDeleteDocumentary = useCallback(
    async (documentaryId: string): Promise<void> => {
      try {
        await documentaryService.softDeleteDocumentary(documentaryId);
      } catch (error) {
        console.error('Failed to delete documentary:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const permanentDeleteDocumentary = useCallback(
    async (documentaryId: string): Promise<void> => {
      try {
        await documentaryService.permanentDeleteDocumentary(documentaryId);
      } catch (error) {
        console.error('Failed to permanently delete documentary:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const getDocumentary = useCallback(
    async (documentaryId: string): Promise<DocumentaryDetailsResponse> => {
      try {
        return await documentaryService.getDocumentary(documentaryId);
      } catch (error) {
        console.error('Failed to fetch documentary:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const updateDocumentaryThumbnail = useCallback(
    async (documentaryId: string, thumbnailFile: File): Promise<Documentary> => {
      try {
        return await documentaryService.updateThumbnail(documentaryId, thumbnailFile);
      } catch (error) {
        console.error('Failed to update documentary thumbnail:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const initiateVideoUpload = useCallback(
    async (
      documentaryId: string,
      payload: InitiateMultipartUploadRequest
    ): Promise<InitiateMultipartUploadResponse> => {
      try {
        return await documentaryService.initiateMultipartVideoUpload(documentaryId, payload);
      } catch (error) {
        console.error('Failed to initiate documentary video upload:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const completeVideoUpload = useCallback(
    async (
      documentaryId: string,
      payload: CompleteMultipartUploadRequest
    ): Promise<VideoVariant> => {
      try {
        return await documentaryService.completeMultipartVideoUpload(documentaryId, payload);
      } catch (error) {
        console.error('Failed to complete documentary video upload:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const getMultipartUploadStatus = useCallback(
    async (
      documentaryId: string,
      key: string,
      uploadId: string
    ): Promise<VideoMultipartStatusResponse> => {
      try {
        return await documentaryService.getMultipartUploadStatus(documentaryId, key, uploadId);
      } catch (error) {
        console.error('Failed to fetch documentary upload status:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const regenerateMultipartPartUrls = useCallback(
    async (
      documentaryId: string,
      payload: VideoMultipartRegenerateRequest
    ): Promise<VideoMultipartPresignResponse> => {
      try {
        return await documentaryService.regenerateMultipartPartUrls(documentaryId, payload);
      } catch (error) {
        console.error('Failed to regenerate documentary multipart URLs:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const abortMultipartUpload = useCallback(
    async (params: { key: string; uploadId: string }): Promise<void> => {
      try {
        await documentaryService.abortMultipartVideoUpload(params);
      } catch (error) {
        console.error('Failed to abort documentary video upload:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  const deleteVideo = useCallback(
    async (documentaryId: string, fileId: string): Promise<void> => {
      try {
        await documentaryService.deleteVideo(documentaryId, fileId);
      } catch (error) {
        console.error('Failed to delete documentary video:', error);
        throw error;
      }
    },
    [documentaryService]
  );

  return {
    createDocumentary,
    createDocumentaryJson,
    updateDocumentary,
    softDeleteDocumentary,
    permanentDeleteDocumentary,
    getDocumentary,
    updateDocumentaryThumbnail,
    initiateVideoUpload,
    completeVideoUpload,
    getMultipartUploadStatus,
    regenerateMultipartPartUrls,
    abortMultipartUpload,
    deleteVideo,
  };
}

